import _ from 'lodash';
import fetchDiagnosisCodes from '../api/services/fetchDiagnosisCodes';
import type { DiagnosisCodeOptions, DiagnosisCodes } from './study.types';

/**
 * Turns fetched diagnosis codes into select options for the diagnosis pickers. Yields an empty array for an empty input, otherwise mapping each code's fullDisplay onto both label and value while carrying its id across.
 *
 * @param diagnosisCodes - Diagnosis codes returned by the query.
 * @returns Option objects carrying label, value and id.
 */
const convertDiagnosisCodes = (diagnosisCodes: DiagnosisCodes[] | null | undefined) => {
  if (!diagnosisCodes?.length) {
    return [];
  }

  return diagnosisCodes.map((item) => ({
    label: item.fullDisplay,
    value: item.fullDisplay,
    id: item.id,
  }));
};

/**
 * Loads diagnosis code options for the primary or secondary picker as the clinician types or scrolls. Returns early when both search terms are empty, derives a cursor from the last loaded id when loading more, converts the fetched codes into options, appends them on load-more and replaces them otherwise, and on failure logs the error and empties the target list.
 *
 * @param type - Which diagnosis picker is being filled.
 * @param isLoadMore - Whether the call appends a further page instead of replacing the list.
 * @param searchPrimary - Search text of the primary picker.
 * @param searchSecond - Search text of the secondary picker.
 * @param diagnosisCodesPrimary - Options already loaded for the primary picker.
 * @param diagnosisCodesSecond - Options already loaded for the secondary picker.
 * @returns A partial state object holding the updated option list, empty when no search text is present.
 */
const handleFetchDiagnosisCodes = async ({
  type,
  isLoadMore = false,
  searchPrimary,
  searchSecond,
  diagnosisCodesPrimary,
  diagnosisCodesSecond,
}: {
  type: 'primary' | 'second';
  isLoadMore?: boolean;
  searchPrimary?: string;
  searchSecond?: string;
  diagnosisCodesPrimary: DiagnosisCodeOptions[];
  diagnosisCodesSecond: DiagnosisCodeOptions[];
}) => {
  if (!searchPrimary && !searchSecond) {
    return {};
  }

  const newState = {};
  const isPrimary = type === 'primary';
  try {
    const searchDiagnosisCode = isPrimary ? searchPrimary : searchSecond;
    const cursor = isLoadMore
      ? isPrimary
        ? diagnosisCodesPrimary.length > 0
          ? diagnosisCodesPrimary[diagnosisCodesPrimary.length - 1].id
          : undefined
        : diagnosisCodesSecond.length > 0
          ? diagnosisCodesSecond[diagnosisCodesSecond.length - 1].id
          : undefined
      : undefined;
    const filter = {
      searchDiagnosisCode: _.trim(searchDiagnosisCode),
      cursor,
    };
    const diagnosisCodes = await fetchDiagnosisCodes(filter);
    // GraphQL codegen types id/fullDisplay as nullable; the query never actually returns nulls here
    const convertedDiagnosisCodes = convertDiagnosisCodes(
      diagnosisCodes as DiagnosisCodes[] | null | undefined,
    );
    if (isPrimary) {
      if (isLoadMore) {
        _.assign(newState, {
          diagnosisCodesPrimary: [...diagnosisCodesPrimary, ...convertedDiagnosisCodes],
        });
      } else {
        _.assign(newState, { diagnosisCodesPrimary: convertedDiagnosisCodes });
      }
    } else if (isLoadMore) {
      _.assign(newState, {
        diagnosisCodesSecond: [...diagnosisCodesSecond, ...convertedDiagnosisCodes],
      });
    } else {
      _.assign(newState, { diagnosisCodesSecond: convertedDiagnosisCodes });
    }
  } catch (error) {
    console.error('Failed to fetch diagnosis codes: ', error);
    if (isPrimary) {
      _.assign(newState, { diagnosisCodesPrimary: [] });
    } else {
      _.assign(newState, { diagnosisCodesSecond: [] });
    }
  }
  return newState;
};

export { convertDiagnosisCodes, handleFetchDiagnosisCodes };
